import React, { useState, useEffect } from 'react'
import { Input, Button, Modal } from 'semantic-ui-react'
import { PatternFormat } from 'react-number-format'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useForm, useField } from 'react-final-form'
import moment from 'moment'
import 'moment/locale/pt-br'
import { ptBR } from 'date-fns/locale/pt-BR'

import { ModalContentWrapper } from '../pure/DateInput/styles'

interface DateInputProps {
  id?: string
  name: string
  readOnly?: boolean
  minDate?: string | Date
  maxDate?: string | Date
  disabled?: boolean
  onChange?: (event: { value: string }) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  autoComplete?: string
}

export const DateInput: React.FC<DateInputProps> = ({
  name,
  readOnly,
  minDate,
  maxDate,
  disabled,
  onChange,
  onBlur,
  id = name,
  autoComplete
}) => {
  const form = useForm()
  const field = useField(name)
  const [modal, setModal] = useState(false)

  useEffect(() => {
    form.registerField(name, () => undefined, {})
    registerLocale('pt-br', ptBR)
  }, [])

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    form.change(name, event.target.value)
  }

  function handleInputBlur(event: React.FocusEvent<HTMLInputElement, Element>) {
    onBlur?.(event)
    form.blur(name)
  }

  function handleModalClose() {
    setModal(false)
  }

  function handlePickerChange(date: Date | null) {
    const formattedDate = date ? moment(date).format('DD/MM/YYYY') : ''
    onChange?.({ value: formattedDate })
    form.change(name, formattedDate)
    setModal(false)
  }

  const selectedDate =
    field.input.value && moment(field.input.value, 'DD/MM/YYYY', true).isValid()
      ? moment(field.input.value, 'DD/MM/YYYY').toDate()
      : null

  return (
    <>
      <Input>
        <PatternFormat
          id={id}
          autoComplete={autoComplete}
          format="##/##/####"
          value={field.input.value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          readOnly={readOnly}
          disabled={disabled}
        />
        <Button
          icon="calendar"
          type="button"
          onClick={() => setModal(true)}
          disabled={readOnly || disabled}
          basic
        />
      </Input>
      <Modal open={modal} onClose={handleModalClose} size="tiny" basic>
        <Modal.Content>
          <ModalContentWrapper>
            <DatePicker
              selected={selectedDate}
              onChange={handlePickerChange}
              dateFormat="dd/MM/yyyy"
              minDate={
                minDate instanceof Date
                  ? minDate
                  : minDate
                    ? new Date(minDate)
                    : undefined
              }
              maxDate={
                maxDate instanceof Date
                  ? maxDate
                  : maxDate
                    ? new Date(maxDate)
                    : undefined
              }
              inline
              locale="pt-br"
            />
          </ModalContentWrapper>
        </Modal.Content>
      </Modal>
    </>
  )
}
